import { supabase } from '@/lib/supabaseClient';
import { ensureAuthenticatedSession } from '@/utils/authentication';
import { User } from '@/types';
import { toApiUser, toFrontUser } from '@/utils/userMappers';
import { assertValidSpanishText, ensureValidEmail, EMAIL_VALIDATION_MESSAGE, normalizeEmail } from '@/utils/validations';
import { buildEdgeUrl } from '@/utils/edgeFunctions';
import { encodeCommunitySecret } from '@/utils/communitySecret';
import { AGE_DECLARATION_VERSION, PRIVACY_POLICY_VERSION } from '@/constants/privacyPolicy';
import type {
  PostgrestMaybeSingleResponse,
  PostgrestResponse,
  PostgrestSingleResponse,
  SupabaseClient,
} from '@supabase/supabase-js';


const USER_SELECT_FIELD_LIST = [
  'id',
  'name',
  'last_name',
  'email',
  'phone',
  'status',
  'created_at',
  'noti_email',
  'noti_push',
  'noti_events',
  'noti_payments',
  'noti_incidents',
  'noti_meetings',
  'noti_agreements',
  'noti_notices',
  'noti_polls',
  'noti_proposals',
  'noti_documents',
  'noti_last',
  'deletion_requested_at',
  'deletion_effective_at',
  'is_blocked',
  'blocked_reason',
];

export const USER_SELECT_FIELDS = `${USER_SELECT_FIELD_LIST.join(', ')}, user_roles_by_community(community_id,is_default,role,block,floor,letter,is_defaulter,participation_quota)`;
export const USER_SELECT_FIELDS_INNER = `${USER_SELECT_FIELD_LIST.join(', ')}, user_roles_by_community!inner(community_id,is_default,role,block,floor,letter,is_defaulter,participation_quota)`;

type UserQueryResponse<T> =
  | PostgrestResponse<T>
  | PostgrestSingleResponse<T>
  | PostgrestMaybeSingleResponse<T>;

type UserMembership = NonNullable<User['communityMemberships']>[number];
export type PrivacyPolicyDeclarationOrigin = 'public_registration' | 'invitation' | 'migration';

type UserCreateInput = Partial<User> &
  Pick<User, 'name' | 'email' | 'role'> & {
    password: string;
    privacyPolicyVersion?: string;
    privacy_policy_version?: string;
    age14OrOlderConfirmed?: boolean;
    age_14_plus_confirmed?: boolean;
    ageDeclarationVersion?: string;
    age_declaration_version?: string;
    privacyPolicyDeclarationOrigin?: PrivacyPolicyDeclarationOrigin;
    privacy_policy_declaration_origin?: PrivacyPolicyDeclarationOrigin;
    communitySecret?: string;
    community_secret?: string;
    secretsByCommunity?: unknown[];
    secrets_by_community?: unknown[];
    communityMemberships?: UserMembership[];
    community_id?: string | null;
    communities?: Array<{ id?: string | null }>;
    captchaToken?: string;
    captcha_token?: string;
    cfTurnstileResponse?: string;
    cf_turnstile_response?: string;
  };

type EdgeResponsePayload = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

const toRecordOrNull = (value: unknown): Record<string, unknown> | null =>
  isRecord(value) ? value : null;

const getErrorPayloadValue = (
  payload: EdgeResponsePayload | null,
  key: string,
): unknown => payload?.[key];

const parseJsonObject = async (response: Response): Promise<EdgeResponsePayload | null> => {
  try {
    const value = await response.json() as unknown;
    return isRecord(value) ? value : null;
  } catch {
    return null;
  }
};

export async function getUsers(): Promise<User[]> {
  await ensureAuthenticatedSession();
  const response = (await supabase
    .from('users')
    .select(USER_SELECT_FIELDS)
    .order('created_at', { ascending: false })) as UserQueryResponse<Record<string, unknown>[]>;
  if (response.error) throw response.error;
  const rows: Record<string, unknown>[] = Array.isArray(response.data) ? response.data.filter(isRecord) : [];
  return rows.map(row => toFrontUser(row));
}

export async function getUser(userId: string): Promise<User | null> {
  await ensureAuthenticatedSession();
  const response = (await supabase
    .from('users')
    .select(USER_SELECT_FIELDS)
    .eq('id', userId)
    .maybeSingle()) as UserQueryResponse<Record<string, unknown> | null>;

  if (response.error) throw response.error;
  const record = toRecordOrNull(response.data);
  return record ? toFrontUser(record) : null;
}

export async function getUsersByCommunity(communityId: string): Promise<User[]> {
  await ensureAuthenticatedSession();
  const response = (await supabase
    .from('users')
    .select(USER_SELECT_FIELDS_INNER)
    .eq('user_roles_by_community.community_id', communityId)) as UserQueryResponse<Record<string, unknown>[]>;

  if (!response.error && Array.isArray(response.data)) {
    return response.data.filter(isRecord).map((row) => toFrontUser(row));
  }

  const membershipResponse = await supabase
    .from('user_roles_by_community')
    .select('user_id')
    .eq('community_id', communityId);

  if (membershipResponse.error) throw membershipResponse.error;

  const membershipRows = Array.isArray(membershipResponse.data)
    ? membershipResponse.data.filter(
        (row): row is { user_id: string } => isRecord(row) && typeof row.user_id === 'string'
      )
    : [];

  const userIds = Array.from(new Set(membershipRows.map((row) => row.user_id)));

  if (userIds.length === 0) {
    return [];
  }

  const fallbackResponse = (await supabase
    .from('users')
    .select(USER_SELECT_FIELDS)
    .in('id', userIds)) as UserQueryResponse<Record<string, unknown>[]>;
  if (fallbackResponse.error) throw fallbackResponse.error;
  const rows: Record<string, unknown>[] = Array.isArray(fallbackResponse.data) ? fallbackResponse.data.filter(isRecord) : [];
  return rows.map((row) => toFrontUser(row));
}

export async function getUsersByIds(ids: string[]): Promise<User[]> {
  await ensureAuthenticatedSession();
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  if (uniqueIds.length === 0) {
    return [];
  }

  const response = (await supabase
    .from('users')
    .select(USER_SELECT_FIELDS)
    .in('id', uniqueIds)) as UserQueryResponse<Record<string, unknown>[]>;

  if (response.error) throw response.error;

  const rows: Record<string, unknown>[] = Array.isArray(response.data) ? response.data.filter(isRecord) : [];
  return rows.map(row => toFrontUser(row));
}

type CreateUserOptions = {
  requireAuth?: boolean;
};

export async function createUser(
  user: UserCreateInput,
  options?: CreateUserOptions
) {
  assertValidSpanishText(user.name, 'El nombre');
  assertValidSpanishText(user.lastName ?? user.last_name, 'El apellido');

  const normalizedEmail = ensureValidEmail(user.email);
  const {
    password,
    id: _id,
    secret,
    communitySecret,
    community_secret,
    privacyPolicyVersion,
    privacy_policy_version,
    privacyPolicyDeclarationOrigin,
    privacy_policy_declaration_origin,
    secretsByCommunity,
    secrets_by_community,
    communityMemberships,
    captchaToken,
    captcha_token,
    cfTurnstileResponse,
    cf_turnstile_response,
    ...rest
  } = { ...user, email: normalizedEmail };

  if (!password || password.length === 0) {
    throw new Error('La contraseña es obligatoria para crear un usuario.');
  }

  const providedSecret = secret ?? communitySecret ?? community_secret;
  const communityIdsFromPayload = Array.isArray(rest.communityIds) ? rest.communityIds : [];
  const fallbackCommunityId =
    rest.communityId ??
    rest.community_id ??
    (Array.isArray(rest.communities) && rest.communities[0]?.id ? rest.communities[0]?.id : undefined);
  const normalizedCommunityIds = Array.from(
    new Set(
      [...communityIdsFromPayload, fallbackCommunityId]
        .map((value) => (typeof value === 'string' ? value.trim() : ''))
        .filter(Boolean)
    )
  );

  if (normalizedCommunityIds.length === 0) {
    throw new Error('Debe seleccionar una comunidad válida.');
  }

  const rawSecretEntries = Array.isArray(secretsByCommunity)
    ? secretsByCommunity
    : Array.isArray(secrets_by_community)
      ? secrets_by_community
      : [];
  const resolvedSecretEntries = normalizedCommunityIds.map((communityId, index) => {
    const matched = rawSecretEntries.find((entry) => {
      if (!isRecord(entry)) {
        return false;
      }

      return entry.community_id === communityId || entry.communityId === communityId;
    });
    const candidate = isRecord(matched)
      ? matched.secret ?? (index === 0 ? providedSecret : undefined)
      : (index === 0 ? providedSecret : undefined);
    return { community_id: communityId, secret: typeof candidate === 'string' ? candidate.trim() : '' };
  });
  if (resolvedSecretEntries.some((entry) => entry.secret.length === 0)) {
    throw new Error('Debes proporcionar el código secreto de cada comunidad.');
  }
  const mappedUser = toApiUser(rest);
  const normalizeNullableString = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  };
  const legacyRole = rest.role;
  const membershipEntries: UserMembership[] = Array.isArray(communityMemberships) && communityMemberships.length > 0
    ? communityMemberships
    : normalizedCommunityIds.map((communityId, index) => ({
        communityId,
        role: legacyRole,
        block: normalizeNullableString(rest.block),
        floor: normalizeNullableString(rest.floor),
        letter: normalizeNullableString(rest.letter),
        isDefault: index === 0,
      }));
  const resolvedPrivacyPolicyVersion = (() => {
    const candidates = [privacyPolicyVersion, privacy_policy_version];
    for (const candidate of candidates) {
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed.length > 0) {
          return trimmed;
        }
      }
    }
    return PRIVACY_POLICY_VERSION;
  })();
  const payload = {
    ...mappedUser,
    email: normalizedEmail,
    password,
    secret: encodeCommunitySecret(resolvedSecretEntries[0].secret),
    community_ids: normalizedCommunityIds,
    secrets_by_community: resolvedSecretEntries.map((entry) => ({
      community_id: entry.community_id,
      secret: encodeCommunitySecret(entry.secret),
    })),
    community_memberships: membershipEntries.map((entry) => ({
      community_id: entry.communityId,
      role: entry.role,
      block: entry.block ?? null,
      floor: entry.floor ?? null,
      letter: entry.letter ?? null,
    })),
  } as Record<string, unknown>;

  // Ensure null values for optional fields that the Edge Function expects explicitly.
  ['phone', 'status'].forEach((field) => {
    if (!(field in payload)) {
      payload[field] = null;
    }
  });

  payload.privacy_policy_version = resolvedPrivacyPolicyVersion;
  payload.age_14_plus_confirmed = user.age14OrOlderConfirmed ?? user.age_14_plus_confirmed;
  payload.age_declaration_version = user.ageDeclarationVersion ?? user.age_declaration_version ?? AGE_DECLARATION_VERSION;
  payload.privacy_policy_declaration_origin =
    privacyPolicyDeclarationOrigin ?? privacy_policy_declaration_origin ?? 'public_registration';

  const resolvedCaptchaToken =
    captchaToken ?? captcha_token ?? cfTurnstileResponse ?? cf_turnstile_response;
  if (resolvedCaptchaToken && resolvedCaptchaToken.trim().length > 0) {
    const trimmedToken = resolvedCaptchaToken.trim();
    payload['cf-turnstile-response'] = trimmedToken;
    payload.cf_turnstile_response = trimmedToken;
    payload.captcha_token = trimmedToken;
  }

  const edgeUrl = buildEdgeUrl('register-user');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (options?.requireAuth !== false) {
    await ensureAuthenticatedSession();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }
  }

  const response = await fetch(edgeUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let errorMessage = 'No se pudo crear el usuario. Inténtalo de nuevo.';
    try {
      const errorBody = await parseJsonObject(response);
      const message = getString(getErrorPayloadValue(errorBody, 'message'));
      if (message) {
        errorMessage = message;
      }
    } catch (error) {
      console.error('Error al analizar la respuesta de la función register-user', error);
    }
    throw new Error(errorMessage);
  }

  const payloadData = await parseJsonObject(response);
  const createdUser = getErrorPayloadValue(payloadData, 'user');
  if (!createdUser || !isRecord(createdUser)) {
    throw new Error('La función de registro no devolvió la información del usuario creado.');
  }

  return toFrontUser(createdUser);
}

export async function updateUser(id: string, updates: Partial<User>) {
  const session = await ensureAuthenticatedSession();
  const updatesRecord = updates as Partial<User> & {
    last_name?: string;
    communityMemberships?: UserMembership[];
  };
  assertValidSpanishText(updates.name, 'El nombre');
  assertValidSpanishText(updates.lastName ?? updatesRecord.last_name, 'El apellido');

  const sanitizedUpdates: Partial<User> = { ...updates };
  const requestedCommunityIds: string[] | null = Array.isArray(updates.communityIds)
    ? Array.from(new Set(updates.communityIds.map((value: unknown) => (typeof value === 'string' ? value.trim() : '')).filter(Boolean)))
    : null;

  delete (sanitizedUpdates as Record<string, unknown>).avatar;
  delete (sanitizedUpdates as Record<string, unknown>).avatarPath;

  let normalizedEmail: string | undefined;
  let existingUserRow: Record<string, unknown> | null = null;
  if ('email' in sanitizedUpdates) {
    const rawEmail = sanitizedUpdates.email;
    if (typeof rawEmail === 'string') {
      if (rawEmail.trim().length === 0) {
        throw new Error(EMAIL_VALIDATION_MESSAGE);
      }
      normalizedEmail = ensureValidEmail(rawEmail);
    } else if (rawEmail !== undefined && rawEmail !== null) {
      throw new Error(EMAIL_VALIDATION_MESSAGE);
    }
    delete sanitizedUpdates.email;
  }

  if ('phone' in sanitizedUpdates) {
    const rawPhone = sanitizedUpdates.phone as User['phone'];
    if (typeof rawPhone === 'string') {
      const trimmedPhone = rawPhone.trim();
      sanitizedUpdates.phone = trimmedPhone.length > 0 ? trimmedPhone : null;
    } else if (rawPhone === null) {
      sanitizedUpdates.phone = null;
    } else if (rawPhone !== undefined) {
      throw new Error('El teléfono debe ser un texto válido.');
    }
  }

  const housingFields: Array<'block' | 'floor' | 'letter'> = ['block', 'floor', 'letter'];
  housingFields.forEach((field) => {
    if (field in sanitizedUpdates) {
      const rawValue = sanitizedUpdates[field];
      if (typeof rawValue === 'string') {
        const trimmedValue = rawValue.trim();
        (sanitizedUpdates as Record<string, unknown>)[field] = trimmedValue.length > 0 ? trimmedValue : null;
      } else if (rawValue === null) {
        (sanitizedUpdates as Record<string, unknown>)[field] = null;
      }
    }
  });

  if (normalizedEmail) {
    const currentUserResponse = (await supabase
      .from('users')
      .select(USER_SELECT_FIELDS)
      .eq('id', id)
      .single()) as UserQueryResponse<Record<string, unknown>>;
    if (currentUserResponse.error) throw currentUserResponse.error;
    existingUserRow = toRecordOrNull(currentUserResponse.data);

    const rawEmailValue = existingUserRow && typeof existingUserRow['email'] === 'string'
      ? (existingUserRow['email'] as string)
      : undefined;
    const currentEmailValue = rawEmailValue ? normalizeEmail(rawEmailValue) : undefined;
    if (currentEmailValue === normalizedEmail) {
      normalizedEmail = undefined;
    }
  }

  const mapped = toApiUser(sanitizedUpdates);
  let data: Record<string, unknown> | null = existingUserRow;
  let shouldRefreshUser = false;

  if (Object.keys(mapped).length > 0) {
    const updateResult = (await supabase
      .from('users')
      .update(mapped)
      .eq('id', id)
      .select(USER_SELECT_FIELDS)
      .single()) as UserQueryResponse<Record<string, unknown>>;

    if (updateResult.error) throw updateResult.error;
    data = toRecordOrNull(updateResult.data) ?? data;
  }

  if (requestedCommunityIds) {
    type RequestedMembership = { communityId: string; role?: User['role']; block?: string | null; floor?: string | null; letter?: string | null };
    const requestedMemberships: RequestedMembership[] = Array.isArray(updatesRecord.communityMemberships) ? updatesRecord.communityMemberships : [];
    const requestedMembershipByCommunityId = new Map<string, RequestedMembership>(
      requestedMemberships
        .filter((membership): membership is RequestedMembership => typeof membership?.communityId === 'string')
        .map((membership) => [membership.communityId, membership])
    );

    const isGlobalAdmin = updates.role === 'admin' && requestedCommunityIds.length === 0;

    let desiredMembershipsToPersist: Array<{
      user_id: string;
      community_id: string | null;
      role: string;
      block: string | null;
      floor: string | null;
      letter: string | null;
      is_default?: boolean;
    }> = [];

    if (isGlobalAdmin) {
      desiredMembershipsToPersist = [{
        user_id: id,
        community_id: null,
        role: 'admin',
        block: null,
        floor: null,
        letter: null,
        is_default: true,
      }];
    } else {
      desiredMembershipsToPersist = requestedCommunityIds.map((communityId, index) => {
        const membership = requestedMembershipByCommunityId.get(communityId);
        const role = membership?.role ?? updates.role ?? 'neighbor';
        const roleWithoutHousing = role === 'admin' || role === 'manager';
        const normalizeHousingValue = (value: unknown) => {
          if (typeof value !== 'string') return null;
          const trimmedValue = value.trim();
          return trimmedValue.length > 0 ? trimmedValue : null;
        };

        return {
          user_id: id,
          community_id: communityId,
          role,
          block: roleWithoutHousing ? null : normalizeHousingValue(membership?.block),
          floor: roleWithoutHousing ? null : normalizeHousingValue(membership?.floor),
          letter: roleWithoutHousing ? null : normalizeHousingValue(membership?.letter),
          is_default: index === 0,
        };
      });
    }

    const { data: existingMemberships, error: membershipsError } = await supabase
      .from('user_roles_by_community')
      .select('id, community_id')
      .eq('user_id', id);
    if (membershipsError) throw membershipsError;

    const existingRows = (existingMemberships ?? []) as Array<{ id: string; community_id: string | null }>;
    const toDeleteIds: string[] = [];
    existingRows.forEach((row) => {
      const match = desiredMembershipsToPersist.find((d) => d.community_id === row.community_id);
      if (!match) {
        toDeleteIds.push(row.id);
      }
    });

    if (toDeleteIds.length > 0) {
      const { error } = await supabase.from('user_roles_by_community').delete().in('id', toDeleteIds);
      if (error) throw error;
    }

    if (desiredMembershipsToPersist.length > 0) {
      const toUpsert = desiredMembershipsToPersist.map((d) => {
        const existing = existingRows.find((r) => r.community_id === d.community_id);
        return {
          ...(existing ? { id: existing.id } : {}),
          ...d,
        };
      });

      const { error } = await supabase
        .from('user_roles_by_community')
        .upsert(toUpsert);
      if (error) throw error;
      shouldRefreshUser = true;
    }
  }

  if (normalizedEmail) {
    const accessToken = session.access_token;
    if (!accessToken) {
      throw new Error('Tu sesión ha expirado. Vuelve a iniciar sesión.');
    }

    await updateUserEmailThroughEdge(id, normalizedEmail, accessToken);
  }

  if (!data || normalizedEmail || shouldRefreshUser) {
    const refreshResponse = (await supabase
      .from('users')
      .select(USER_SELECT_FIELDS)
      .eq('id', id)
      .single()) as UserQueryResponse<Record<string, unknown>>;
    if (refreshResponse.error) throw refreshResponse.error;
    data = toRecordOrNull(refreshResponse.data);
  }

  if (!data) {
    throw new Error('No se pudo obtener la información actualizada del usuario.');
  }

  return toFrontUser(data);
}

const EDGE_UPDATE_EMAIL_PATH = 'update-user-email';

const EDGE_UPDATE_EMAIL_ERROR_MESSAGES: Record<string, string> = {
  'Invalid email format': EMAIL_VALIDATION_MESSAGE,
  'Missing Authorization header': 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
  'Invalid or expired token': 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
  'Requester not found in users table': 'Tu usuario no tiene permisos para realizar esta acción.',
  "Only admins can update other users' emails": 'Solo los administradores pueden actualizar el correo de otros usuarios.',
  'Target auth user not found': 'No se encontró la cuenta de autenticación del usuario.',
  "Target user not found in 'users' table": 'No se encontró el usuario que intentas actualizar.',
  'New email equals current email': 'El nuevo email debe ser diferente al actual.',
  'Failed to update email in Supabase Auth': 'No se pudo actualizar el email en Supabase Auth. Inténtalo de nuevo.',
  "Updated in Auth but failed to update 'users' table": 'El email se actualizó parcialmente. Contacta al soporte.',
  'Missing Supabase environment variables': 'Servicio de actualización de email no disponible. Contacta al soporte.'
};

function translateEdgeUpdateEmailError(payload: EdgeResponsePayload | null): string {
  if (!payload) {
    return 'No se pudo actualizar el email del usuario. Inténtalo de nuevo.';
  }

  if (payload.partial) {
    const partialMessage = EDGE_UPDATE_EMAIL_ERROR_MESSAGES["Updated in Auth but failed to update 'users' table"];
    return partialMessage ?? 'El email se actualizó parcialmente. Contacta al soporte.';
  }

  const errorKey =
    getErrorPayloadValue(payload, 'error') ??
    getErrorPayloadValue(payload, 'message') ??
    getErrorPayloadValue(payload, 'details') ??
    getErrorPayloadValue(payload, 'code');
  if (typeof errorKey === 'string') {
    const trimmedKey = errorKey.trim();
    if (EDGE_UPDATE_EMAIL_ERROR_MESSAGES[trimmedKey]) {
      return EDGE_UPDATE_EMAIL_ERROR_MESSAGES[trimmedKey];
    }
    if (trimmedKey.length > 0) {
      return trimmedKey;
    }
  }

  return 'No se pudo actualizar el email del usuario. Inténtalo de nuevo.';
}

async function updateUserEmailThroughEdge(userId: string, newEmail: string, accessToken: string): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  let response: Response;
  try {
    response = await fetch(buildEdgeUrl(EDGE_UPDATE_EMAIL_PATH), {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId, newEmail }),
    });
  } catch (error) {
    console.error('Error al invocar la función edge update-user-email', error);
    throw new Error('No se pudo conectar con el servicio de actualización de email. Inténtalo de nuevo.');
  }

  let payload: EdgeResponsePayload | null = null;
  try {
    payload = await parseJsonObject(response);
  } catch (error) {
    if (!response.ok) {
      console.error('Error al analizar la respuesta de update-user-email', error);
      throw new Error('No se pudo actualizar el email del usuario. Inténtalo de nuevo.');
    }
  }

  if (!response.ok || !payload?.success) {
    throw new Error(translateEdgeUpdateEmailError(payload));
  }
}

const EDGE_INVITE_HOUSEHOLD_PATH = 'invite-household-member';
const EDGE_LIST_HOUSEHOLD_INVITATIONS_PATH = 'list-household-invitations';

const EDGE_INVITE_ERROR_MESSAGES: Record<string, string> = {
  validation_error: 'Completa el nombre y el email del usuario a invitar.',
  invalid_email: EMAIL_VALIDATION_MESSAGE,
  invalid_role: 'Selecciona un rol válido para el usuario invitado.',
  missing_authorization: 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
  invalid_token: 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
  requester_not_found: 'No se encontró tu perfil. Contacta al soporte.',
  forbidden: 'Solo los vecinos, presidentes y vocales pueden invitar a miembros de su domicilio.',
  missing_house_data: 'Completa los datos de bloque, piso y letra en tu perfil antes de invitar.',
  user_exists: 'Este email ya pertenece a un usuario registrado.',
  auth_user_exists: 'Ya existe una cuenta asociada a este email.',
  auth_lookup_failed: 'No se pudo validar el email en autenticación. Inténtalo de nuevo.',
  invite_failed: 'No se pudo enviar la invitación. Inténtalo de nuevo.',
  invitation_record_failed: 'No se pudo registrar la invitación. Inténtalo de nuevo.',
  profile_creation_failed: 'No se pudo crear el perfil del nuevo usuario. Contacta al soporte.',
  configuration_error: 'El servicio de invitaciones no está disponible. Contacta al soporte.'
};

const EDGE_INVITATIONS_LIST_ERROR_MESSAGES: Record<string, string> = {
  missing_authorization: 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
  invalid_token: 'Tu sesión ha expirado. Vuelve a iniciar sesión.',
  requester_not_found: 'No se encontró tu perfil. Contacta al soporte.',
  forbidden: 'No tienes permisos para consultar invitaciones.',
  invitation_list_failed: 'No se pudieron cargar las invitaciones. Inténtalo de nuevo.',
  configuration_error: 'El servicio de invitaciones no está disponible. Contacta al soporte.'
};

function translateEdgeInviteError(payload: EdgeResponsePayload | null): string {
  if (!payload) {
    return 'No se pudo enviar la invitación. Inténtalo de nuevo.';
  }

  const key = getErrorPayloadValue(payload, 'error') ?? getErrorPayloadValue(payload, 'message');
  if (typeof key === 'string') {
    const trimmedKey = key.trim();
    if (EDGE_INVITE_ERROR_MESSAGES[trimmedKey]) {
      return EDGE_INVITE_ERROR_MESSAGES[trimmedKey];
    }
    if (trimmedKey.length > 0) {
      return trimmedKey;
    }
  }

  return 'No se pudo enviar la invitación. Inténtalo de nuevo.';
}

function translateEdgeInvitationsListError(payload: EdgeResponsePayload | null): string {
  if (!payload) {
    return 'No se pudieron cargar las invitaciones. Inténtalo de nuevo.';
  }

  const key = getErrorPayloadValue(payload, 'error') ?? getErrorPayloadValue(payload, 'message');
  if (typeof key === 'string') {
    const trimmedKey = key.trim();
    if (EDGE_INVITATIONS_LIST_ERROR_MESSAGES[trimmedKey]) {
      return EDGE_INVITATIONS_LIST_ERROR_MESSAGES[trimmedKey];
    }
    if (trimmedKey.length > 0) {
      return trimmedKey;
    }
  }

  return 'No se pudieron cargar las invitaciones. Inténtalo de nuevo.';
}

export interface HouseholdInvitationSummary {
  id: string;
  email: string;
  name: string;
  lastName: string | null;
  role: 'neighbor' | 'renter';
  status: HouseholdInvitationStatus;
  createdAt: string;
  isInvitePending: boolean;
}

export type HouseholdInvitationStatus = 'pending' | 'accepted' | 'not_finished' | 'deleted';

const toHouseholdInvitationStatus = (value: unknown): HouseholdInvitationStatus => {
  if (value === 'pending' || value === 'accepted' || value === 'not_finished' || value === 'deleted') {
    return value;
  }

  return 'pending';
};

const toInviteRole = (value: unknown): 'neighbor' | 'renter' =>
  value === 'renter' ? 'renter' : 'neighbor';

const toHouseholdInvitationSummary = (value: unknown): HouseholdInvitationSummary | null => {
  if (!isRecord(value)) {
    return null;
  }

  const id = getString(value.id);
  const email = getString(value.email);
  const name = getString(value.name);
  const createdAt = getString(value.createdAt);

  if (!id || !email || !name || !createdAt) {
    return null;
  }

  return {
    id,
    email,
    name,
    lastName: typeof value.lastName === 'string' ? value.lastName : null,
    role: toInviteRole(value.role),
    status: toHouseholdInvitationStatus(value.status),
    createdAt,
    isInvitePending: value.isInvitePending === true,
  };
};

export async function inviteHouseholdMember(payload: {
  name: string;
  lastName?: string;
  email: string;
  role: 'neighbor' | 'renter';
}) {
  await ensureAuthenticatedSession();
  const trimmedName = payload.name.trim();
  if (!trimmedName) {
    throw new Error('El nombre es obligatorio para enviar la invitación.');
  }
  assertValidSpanishText(trimmedName, 'El nombre');
  if (payload.lastName) {
    assertValidSpanishText(payload.lastName, 'El apellido');
  }

  const normalizedEmail = ensureValidEmail(payload.email);
  if (!['neighbor', 'renter'].includes(payload.role)) {
    throw new Error('Selecciona un rol válido para el usuario invitado.');
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error('No se encontró una sesión válida para enviar la invitación.');
  }

  let response: Response;
  try {
    response = await fetch(buildEdgeUrl(EDGE_INVITE_HOUSEHOLD_PATH), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        name: trimmedName,
        lastName: payload.lastName?.trim() || null,
        email: normalizedEmail,
        role: payload.role,
      }),
    });
  } catch (error) {
    console.error('Error al invocar la función edge invite-household-member', error);
    throw new Error('No se pudo conectar con el servicio de invitaciones. Inténtalo de nuevo.');
  }

  let responsePayload: EdgeResponsePayload | null = null;
  try {
    responsePayload = await parseJsonObject(response);
  } catch (error) {
    if (!response.ok) {
      console.error('Error al analizar la respuesta de invite-household-member', error);
      throw new Error('No se pudo enviar la invitación. Inténtalo de nuevo.');
    }
  }

  if (!response.ok || !responsePayload?.success) {
    throw new Error(translateEdgeInviteError(responsePayload));
  }

  return;
}

export async function listHouseholdInvitations(): Promise<HouseholdInvitationSummary[]> {
  await ensureAuthenticatedSession();

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error('No se encontró una sesión válida para consultar las invitaciones.');
  }

  let response: Response;
  try {
    response = await fetch(buildEdgeUrl(EDGE_LIST_HOUSEHOLD_INVITATIONS_PATH), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch (error) {
    console.error('Error al invocar la función edge list-household-invitations', error);
    throw new Error('No se pudo conectar con el servicio de invitaciones. Inténtalo de nuevo.');
  }

  let responsePayload: EdgeResponsePayload | null = null;
  try {
    responsePayload = await parseJsonObject(response);
  } catch (error) {
    if (!response.ok) {
      console.error('Error al analizar la respuesta de list-household-invitations', error);
      throw new Error('No se pudieron cargar las invitaciones. Inténtalo de nuevo.');
    }
  }

  if (!response.ok || !responsePayload?.success) {
    throw new Error(translateEdgeInvitationsListError(responsePayload));
  }

  const invitations = responsePayload?.invitations;
  if (!Array.isArray(invitations)) {
    return [];
  }

  return invitations
    .map(toHouseholdInvitationSummary)
    .filter((invitation): invitation is HouseholdInvitationSummary => invitation !== null);
}

const EDGE_DELETE_USER_PATH = 'delete-user';

const EDGE_DELETE_ERROR_MESSAGES: Record<string, string> = {
  'Only admins can delete users': 'Solo los administradores pueden eliminar usuarios.',
  'Admins cannot delete themselves': 'No puedes eliminar tu propio usuario.',
  'Cannot delete another admin': 'No es posible eliminar a otro usuario administrador.',
  'Target user not found': 'No se encontró al usuario que intentas eliminar.',
  "Failed to delete user from 'users' table": 'No se pudo eliminar el usuario de la tabla principal. Inténtalo de nuevo.',
  "Deleted from 'users' table but failed to delete from Auth": 'Se eliminó el registro del usuario, pero no su cuenta de autenticación. Contacta al soporte.',
  'Requester not found in users table': 'Tu usuario no tiene permisos para realizar esta acción.',
  'Invalid or expired token': 'Tu sesión no es válida o ha expirado. Vuelve a iniciar sesión.',
};

function translateEdgeDeleteError(payload: EdgeResponsePayload | null): string {
  if (!payload) {
    return 'No se pudo eliminar el usuario. Inténtalo de nuevo.';
  }

  const errorKey =
    getErrorPayloadValue(payload, 'error') ??
    getErrorPayloadValue(payload, 'message') ??
    getErrorPayloadValue(payload, 'details');
  if (typeof errorKey === 'string' && EDGE_DELETE_ERROR_MESSAGES[errorKey]) {
    return EDGE_DELETE_ERROR_MESSAGES[errorKey];
  }

  if (payload.partial) {
    return EDGE_DELETE_ERROR_MESSAGES["Deleted from 'users' table but failed to delete from Auth"];
  }

  if (typeof errorKey === 'string' && errorKey.trim().length > 0) {
    return errorKey;
  }

  return 'No se pudo eliminar el usuario. Inténtalo de nuevo.';
}

export async function deleteUser(id: string) {
  await ensureAuthenticatedSession();

  const {
    data: { session },
  } = await supabase.auth.getSession();

  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error('No se encontró una sesión válida para eliminar el usuario.');
  }

  const response = await fetch(buildEdgeUrl(EDGE_DELETE_USER_PATH), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ userId: id }),
  });

  const responseText = await response.text();
  let payload: EdgeResponsePayload | null = null;

  if (responseText) {
    try {
      const parsed = JSON.parse(responseText) as unknown;
      payload = isRecord(parsed) ? parsed : null;
    } catch (error) {
      console.error('Error al analizar la respuesta de delete-user', error);
    }
  }

  if (!response.ok) {
    throw new Error(translateEdgeDeleteError(payload));
  }

  if (payload && payload.success !== true) {
    throw new Error(translateEdgeDeleteError(payload));
  }

  return true;
}

export async function logPrivacyAndAgeConsent(
  userId: string,
  policyVersion: string = PRIVACY_POLICY_VERSION,
  ageDeclarationVersion: string = AGE_DECLARATION_VERSION,
  origin: PrivacyPolicyDeclarationOrigin = 'public_registration',
) {
  const { error } = await supabase.from('privacy_policy').upsert(
    {
      user_id: userId,
      policy_version: policyVersion,
      age_14_plus_confirmed: true,
      age_declaration_version: ageDeclarationVersion,
      age_declaration_origin: origin,
      age_declared_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' }
  );

  if (error) {
    console.error('Error al registrar la evidencia de edad y privacidad:', error);
    throw new Error('No se pudo registrar la evidencia de la declaración de edad.');
  }
}

export async function confirmPendingAgeDeclaration(
  policyVersion: string = PRIVACY_POLICY_VERSION,
  ageDeclarationVersion: string = AGE_DECLARATION_VERSION,
  origin: PrivacyPolicyDeclarationOrigin = 'migration',
  customClient?: SupabaseClient,
): Promise<void> {
  const clientToUse = customClient ?? supabase;
  if (customClient) {
    const { data, error } = await customClient.auth.getSession();
    if (error || !data.session) {
      throw new Error('No se encontró una sesión activa para confirmar la declaración de edad.');
    }
  } else {
    await ensureAuthenticatedSession();
  }

  const { error } = await clientToUse.rpc('confirm_age_declaration', {
    p_policy_version: policyVersion,
    p_age_declaration_version: ageDeclarationVersion,
    p_declaration_origin: origin,
  });

  if (error) {
    console.error('Error al confirmar la declaración de edad pendiente:', error);
    throw new Error('No se pudo registrar la declaración de edad. Inténtalo de nuevo.');
  }
}

export async function rejectPendingAgeDeclaration(
  policyVersion: string = PRIVACY_POLICY_VERSION,
  ageDeclarationVersion: string = AGE_DECLARATION_VERSION,
  origin: PrivacyPolicyDeclarationOrigin = 'migration',
  customClient?: SupabaseClient,
): Promise<void> {
  const clientToUse = customClient ?? supabase;
  if (customClient) {
    const { data, error } = await customClient.auth.getSession();
    if (error || !data.session) {
      throw new Error('No se encontró una sesión activa para registrar la declaración de edad.');
    }
  } else {
    await ensureAuthenticatedSession();
  }

  const { error } = await clientToUse.rpc('reject_age_declaration', {
    p_policy_version: policyVersion,
    p_age_declaration_version: ageDeclarationVersion,
    p_declaration_origin: origin,
  });

  if (error) {
    console.error('Error al registrar la declaración de menor de 14 años:', error);
    throw new Error('No se pudo registrar la declaración de edad. Inténtalo de nuevo.');
  }
}

export interface UpdateDwellingDefaulterStatusParams {
  communityId: string;
  block?: string | null;
  floor?: string | null;
  letter?: string | null;
  userId?: string | null;
  isDefaulter: boolean;
}

export async function updateDwellingDefaulterStatus(
  params: UpdateDwellingDefaulterStatusParams,
  customClient?: SupabaseClient,
): Promise<void> {
  const clientToUse = customClient ?? supabase;
  if (customClient) {
    const { data, error } = await customClient.auth.getSession();
    if (error || !data.session) {
      throw new Error('No se encontró una sesión activa para actualizar el estado de morosidad.');
    }
  } else {
    await ensureAuthenticatedSession();
  }

  const { error } = await clientToUse.rpc('set_dwelling_defaulter_status', {
    p_community_id: params.communityId,
    p_block: params.block ?? null,
    p_floor: params.floor ?? null,
    p_letter: params.letter ?? null,
    p_user_id: params.userId ?? null,
    p_is_defaulter: params.isDefaulter,
  });

  if (error) {
    console.error('Error al actualizar el estado de morosidad:', error);
    throw new Error(error.message || 'No se pudo actualizar el estado de morosidad.');
  }
}

export interface UpdateDwellingParticipationQuotaParams {
  communityId: string;
  block?: string | null;
  floor?: string | null;
  letter?: string | null;
  userId?: string | null;
  participationQuota: number | null;
}

export async function updateDwellingParticipationQuota(
  params: UpdateDwellingParticipationQuotaParams,
  customClient?: SupabaseClient,
): Promise<void> {
  const clientToUse = customClient ?? supabase;
  if (customClient) {
    const { data, error } = await customClient.auth.getSession();
    if (error || !data.session) {
      throw new Error('No se encontró una sesión activa para actualizar la cuota de participación.');
    }
  } else {
    await ensureAuthenticatedSession();
  }

  const { error } = await clientToUse.rpc('set_dwelling_participation_quota', {
    p_community_id: params.communityId,
    p_block: params.block ?? null,
    p_floor: params.floor ?? null,
    p_letter: params.letter ?? null,
    p_user_id: params.userId ?? null,
    p_participation_quota: params.participationQuota,
  });

  if (error) {
    console.error('Error al actualizar la cuota de participación:', error);
    throw new Error(error.message || 'No se pudo actualizar la cuota de participación.');
  }
}



